import { FC } from "react";

import type { PopperProps } from "@mui/material/Popper";

import {
  BaseCampaignAutocompleteProps,
  CampaignAutocompleteOption,
  useCampaignAutocompleteOptions,
  useCampaignGroupBy,
  renderCampaignGroup,
} from "./campaignAutocompleteShared";

import { useTranslate } from "@/common/hooks/useTranslate";
import { TableQuickFilterInput } from "@/components/CustomizableTable/TableFilters/TableQuickFilterInput";
import { DashboardAutocomplete } from "@/dashboard-design-system/Autocomplete/DashboardAutocomplete";

interface MultiCampaignAutocompleteProps extends BaseCampaignAutocompleteProps {
  value: string[];
  onChange: (values: string[]) => void;
  onLabelsChange?: (labels: string[]) => void;
  dropdownWidth?: number;
  dropdownPlacement?: PopperProps["placement"];
  isQuickFilter?: boolean;
}

export const MultiCampaignAutocomplete: FC<MultiCampaignAutocompleteProps> = ({
  campaignType = "all",
  value,
  onChange,
  onLabelsChange,
  label,
  placeholder,
  size = "medium",
  autoFocus,
  openOnFocus,
  sx,
  options,
  disabled,
  dropdownWidth,
  dropdownPlacement,
  isQuickFilter = false,
  "data-test": dataTest,
}) => {
  const { t } = useTranslate();
  const { campaignOptions } = useCampaignAutocompleteOptions({
    campaignType,
    options,
  });
  const groupBy = useCampaignGroupBy();
  const InputComponent = isQuickFilter
    ? TableQuickFilterInput<CampaignAutocompleteOption>
    : DashboardAutocomplete<CampaignAutocompleteOption, true, false, false>;

  const selectedOptions = value.flatMap((itemValue) => {
    const match = campaignOptions.find((option) => option.value === itemValue);
    return match ? [match] : [];
  });

  const handleChange = (newValue: CampaignAutocompleteOption[] | undefined) => {
    onChange(newValue ? newValue.map((item) => item.value) : []);
    onLabelsChange?.(newValue ? newValue.map((item) => item.label) : []);
  };

  return (
    <InputComponent
      checkbox
      multiple
      options={campaignOptions}
      value={selectedOptions}
      onChange={handleChange}
      groupBy={groupBy}
      label={label}
      placeholder={placeholder ?? t("dashboard", "contactList.column.form")}
      size={size}
      disableClearable={false}
      disableCloseOnSelect
      autoFocus={autoFocus}
      openOnFocus={openOnFocus}
      sx={sx}
      slotProps={
        dropdownWidth === undefined && dropdownPlacement === undefined
          ? undefined
          : {
              popper: {
                ...(dropdownWidth === undefined ? {} : { style: { width: dropdownWidth, maxWidth: "100%" } }),
                ...(dropdownPlacement === undefined ? {} : { placement: dropdownPlacement }),
              },
            }
      }
      disabled={disabled}
      data-test={dataTest}
      renderGroup={renderCampaignGroup}
    />
  );
};
